import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { config } from '../../config';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '../ui/command';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  Search,
  Sparkles,
  BookOpen,
  Shuffle,
  Star,
  Clock,
  TrendingUp,
  Heart,
  Loader2,
  FileText
} from 'lucide-react';

interface SearchResult {
  mandala: number;
  hymn: number;
  verse: number;
  sanskrit?: string;
  transliteration?: string;
  translation?: string;
  match_type?: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectMandala: (mandalaNumber: number) => void;
  onSelectHymn?: (mandalaNumber: number, hymnNumber: number) => void;
  onRandomVerse: () => void;
  onFeaturedVerse: () => void;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onOpenChange,
  onSelectMandala,
  onSelectHymn,
  onRandomVerse,
  onFeaturedVerse,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const debouncedSearch = useDebounce(inputValue, 300);

  // Handle keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  // Clear search when dialog closes
  useEffect(() => {
    if (!open) {
      setInputValue('');
      setSearchResults([]);
      setSearchError(null);
    }
  }, [open]);

  // Perform search when debounced value changes
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearch || debouncedSearch.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      setSearchError(null);

      try {
        const response = await axios.get(`${config.apiBaseUrl}/search`, {
          params: { query: debouncedSearch }
        });
        setSearchResults(response.data.results || []);
      } catch (err) {
        console.error('Search error:', err);
        setSearchError('Search failed. Please try again.');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearch]);

  const handleSelectMandala = useCallback((num: number) => {
    onSelectMandala(num);
    onOpenChange(false);
  }, [onSelectMandala, onOpenChange]);

  const handleSelectResult = useCallback((result: SearchResult) => {
    if (onSelectHymn) {
      onSelectHymn(result.mandala, result.hymn);
    } else {
      onSelectMandala(result.mandala);
    }
    onOpenChange(false);
  }, [onSelectMandala, onSelectHymn, onOpenChange]);

  // Group search results by mandala
  const groupedResults = useMemo(() => {
    const groups: Record<number, SearchResult[]> = {};
    searchResults.slice(0, 20).forEach(result => {
      if (!groups[result.mandala]) {
        groups[result.mandala] = [];
      }
      groups[result.mandala].push(result);
    });
    return groups;
  }, [searchResults]);

  const hasSearchQuery = inputValue.length >= 2;
  const showSearchResults = hasSearchQuery && (searchResults.length > 0 || isSearching || searchError);

  // Highlight matching text
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!text || !query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 dark:bg-yellow-900/50 rounded px-0.5">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search the Vedas... (mandalas, hymns, topics)"
        value={inputValue}
        onValueChange={setInputValue}
      />
      <CommandList>
        {/* Loading State */}
        {isSearching && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-museum-400" />
            <span className="ml-2 text-sm text-museum-500">Searching...</span>
          </div>
        )}

        {/* Error State */}
        {searchError && !isSearching && (
          <div className="flex flex-col items-center py-6 text-center">
            <p className="text-sm text-red-500">{searchError}</p>
          </div>
        )}

        {/* Search Results */}
        {showSearchResults && !isSearching && !searchError && (
          <>
            {searchResults.length === 0 ? (
              <CommandEmpty>
                <div className="flex flex-col items-center py-6 text-center">
                  <Search className="w-12 h-12 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">No results found for "{inputValue}"</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Try searching for different keywords
                  </p>
                </div>
              </CommandEmpty>
            ) : (
              <>
                <CommandGroup heading={`Search Results (${searchResults.length})`}>
                  {Object.entries(groupedResults).map(([mandala, results]) => (
                    <React.Fragment key={mandala}>
                      {results.map((result, idx) => (
                        <CommandItem
                          key={`${result.mandala}-${result.hymn}-${result.verse}-${idx}`}
                          value={`${result.mandala}.${result.hymn}.${result.verse}`}
                          onSelect={() => handleSelectResult(result)}
                          className="group flex-col items-start gap-1 py-3"
                        >
                          <div className="flex items-center gap-2 w-full">
                            <FileText className="h-4 w-4 text-museum-400 flex-shrink-0" />
                            <span className={cn(
                              "font-mono text-[10px] uppercase tracking-wider",
                              "text-museum-500 dark:text-museum-400"
                            )}>
                              {result.mandala}.{result.hymn}.{result.verse}
                            </span>
                            <Badge variant="outline" className="ml-auto text-[9px]">
                              Mandala {result.mandala}
                            </Badge>
                          </div>
                          {result.translation && (
                            <p className={cn(
                              "text-sm text-museum-600 dark:text-museum-300",
                              "line-clamp-2 pl-6"
                            )}>
                              {highlightMatch(result.translation.slice(0, 150), inputValue)}
                              {result.translation.length > 150 && '...'}
                            </p>
                          )}
                        </CommandItem>
                      ))}
                    </React.Fragment>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
          </>
        )}

        {/* Default Content (when not searching) */}
        {!showSearchResults && (
          <>
            <CommandEmpty>
              <div className="flex flex-col items-center py-6 text-center">
                <Search className="w-12 h-12 text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">Start typing to search</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Search verses, topics, or select a mandala below
                </p>
              </div>
            </CommandEmpty>

            {/* Quick Actions */}
            <CommandGroup heading="Quick Actions">
              <CommandItem
                onSelect={() => {
                  onRandomVerse();
                  onOpenChange(false);
                }}
                className="group"
              >
                <Shuffle className="mr-3 h-4 w-4 text-muted-foreground group-aria-selected:text-primary" />
                <span>Random Verse</span>
                <Badge variant="celestial" className="ml-auto">
                  Discover
                </Badge>
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  onFeaturedVerse();
                  onOpenChange(false);
                }}
                className="group"
              >
                <Star className="mr-3 h-4 w-4 text-muted-foreground group-aria-selected:text-primary" />
                <span>Featured Verse of the Day</span>
                <CommandShortcut>Curated</CommandShortcut>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            {/* Mandalas */}
            <CommandGroup heading="Mandalas (Books)">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                <CommandItem
                  key={num}
                  value={`mandala ${num}`}
                  onSelect={() => handleSelectMandala(num)}
                  className="group"
                >
                  <div className={cn(
                    "flex items-center justify-center w-6 h-6 rounded-full mr-3",
                    "bg-museum-100 dark:bg-museum-800",
                    "text-museum-600 dark:text-museum-300",
                    "text-xs font-bold",
                    "group-aria-selected:bg-museum-900 group-aria-selected:text-white",
                    "dark:group-aria-selected:bg-museum-100 dark:group-aria-selected:text-museum-900",
                    "transition-colors"
                  )}>
                    {num}
                  </div>
                  <span>Mandala {num}</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {getMandalaHymnCount(num)} hymns
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator />

            {/* Popular Topics */}
            <CommandGroup heading="Popular Topics">
              {popularTopics.map((topic) => (
                <CommandItem
                  key={topic.name}
                  value={topic.name}
                  onSelect={() => {
                    setInputValue(topic.searchTerm);
                  }}
                  className="group"
                >
                  <topic.icon className="mr-3 h-4 w-4 text-muted-foreground group-aria-selected:text-primary" />
                  <span>{topic.name}</span>
                  <CommandShortcut>{topic.verses} verses</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>

      {/* Footer */}
      <div className={cn(
        "flex items-center justify-between border-t px-4 py-3",
        "text-xs text-museum-500 dark:text-museum-400",
        "bg-museum-50 dark:bg-museum-900"
      )}>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-museum-200 dark:bg-museum-700 font-mono text-[10px]">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-museum-200 dark:bg-museum-700 font-mono text-[10px]">↵</kbd>
            Select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-museum-200 dark:bg-museum-700 font-mono text-[10px]">esc</kbd>
            Close
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-museum-400" />
          <span className="font-mono text-[10px] uppercase tracking-wider">Veda Explorer</span>
        </div>
      </div>
    </CommandDialog>
  );
};

// Helper data
const getMandalaHymnCount = (mandala: number): number => {
  const counts: Record<number, number> = {
    1: 191, 2: 43, 3: 62, 4: 58, 5: 87,
    6: 75, 7: 104, 8: 103, 9: 114, 10: 191
  };
  return counts[mandala] || 0;
};

const popularTopics = [
  { name: 'Agni (Fire)', icon: TrendingUp, verses: 218, searchTerm: 'agni' },
  { name: 'Indra (Thunder)', icon: Sparkles, verses: 289, searchTerm: 'indra' },
  { name: 'Soma (Sacred Drink)', icon: Heart, verses: 144, searchTerm: 'soma' },
  { name: 'Dawn Hymns', icon: Clock, verses: 52, searchTerm: 'dawn usas' },
  { name: 'Creation Hymns', icon: BookOpen, verses: 28, searchTerm: 'creation' },
];

export default CommandPalette;
